import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  InputNumber,
  Input,
  Alert,
  Space,
  Typography,
  message,
  Divider
} from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { inventoryAPI, warehouseAPI } from '../services/api.ts';

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

interface Warehouse {
  id: number;
  description: string;
  cod_mag?: string;
  is_nationalized: boolean;
}

interface WarehouseTransferModalProps {
  visible: boolean;
  stockId: number | null;
  productDescription?: string;
  currentWarehouseId?: number;
  currentWarehouseName?: string;
  availableQuantity: number;
  isNationalWarehouse?: boolean;
  onOk: () => void;
  onCancel: () => void;
}

const WarehouseTransferModal: React.FC<WarehouseTransferModalProps> = ({
  visible,
  stockId,
  productDescription,
  currentWarehouseId,
  currentWarehouseName,
  availableQuantity,
  isNationalWarehouse = false,
  onOk,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);

  useEffect(() => {
    if (visible) {
      loadWarehouses();
      form.resetFields();
    }
  }, [visible]);

  const loadWarehouses = async () => {
    try {
      const data = await warehouseAPI.getWarehouses();
      setWarehouses(data);
    } catch (error) {
      message.error('Errore nel caricamento dei magazzini');
    }
  };

  // Tipo del magazzino di ORIGINE ricavato dall'anagrafica magazzini (la pagina
  // Inventario non lo passa): prima la finestra considerava doganale ogni origine e
  // bloccava qualsiasi trasferimento verso un magazzino nazionale, anche nazionale→nazionale
  const originWarehouse = warehouses.find(w => w.id === currentWarehouseId);
  const originIsNational = originWarehouse ? originWarehouse.is_nationalized : isNationalWarehouse;
  const originIsCustoms = !!originWarehouse && !originWarehouse.is_nationalized;

  const handleWarehouseSelect = (warehouseId: number) => {
    const warehouse = warehouses.find(w => w.id === warehouseId);
    setSelectedWarehouse(warehouse || null);
  };

  const validateTransfer = (): string | null => {
    const quantity = form.getFieldValue('quantity');
    const destinationId = form.getFieldValue('destination_warehouse_id');

    if (!quantity || quantity <= 0) {
      return 'La quantità deve essere maggiore di 0';
    }

    if (quantity > availableQuantity) {
      return `Quantità insufficiente. Disponibile: ${availableQuantity}`;
    }

    if (!destinationId) {
      return 'Seleziona il magazzino di destinazione';
    }

    if (destinationId === currentWarehouseId) {
      return 'Il magazzino di destinazione deve essere diverso da quello di origine';
    }

    // Validazione doganale → nazionale (la merce estera va prima sdoganata con un IM4)
    if (!originIsNational && selectedWarehouse?.is_nationalized) {
      return 'Non è possibile trasferire da magazzino doganale a nazionale senza sdoganamento: usare "Nazionalizza Prodotti" in Documenti di Carico (documento H1 / IM4).';
    }

    return null;
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();

      const validationError = validateTransfer();
      if (validationError) {
        message.error(validationError);
        return;
      }

      if (!stockId) return;

      setLoading(true);

      const values = form.getFieldsValue();
      const transferData = {
        destination_warehouse_id: values.destination_warehouse_id,
        quantity: values.quantity,
        reason: values.reason,
        notes: values.notes
      };

      await inventoryAPI.transferStockWarehouse(stockId, transferData);

      message.success('Stock trasferito con successo');
      form.resetFields();
      onOk();
    } catch (error: any) {
      console.error('Errore nel trasferimento:', error);
      message.error(error.response?.data?.detail || 'Errore durante il trasferimento');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredWarehouses = () => {
    return warehouses.filter(w => w.id !== currentWarehouseId);
  };

  return (
    <Modal
      title={
        <Space>
          <SwapOutlined />
          <span>Trasferimento Magazzino</span>
        </Space>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      okText="Trasferisci"
      cancelText="Annulla"
      width={600}
    >
      <Divider />

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          message={
            <Space direction="vertical" size="small">
              <Text strong>Prodotto: {productDescription}</Text>
              <Text>Magazzino Origine: {currentWarehouseName}</Text>
              <Text>Quantità Disponibile: <strong>{availableQuantity}</strong></Text>
            </Space>
          }
          type="info"
        />

        {originIsCustoms && (
          <Alert
            message="Merce in magazzino doganale: si può trasferire solo verso un altro magazzino doganale. Per portarla in un magazzino nazionale va sdoganata con un H1 / IM4 (Nazionalizza Prodotti in Documenti di Carico)."
            type="warning"
            showIcon
          />
        )}

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            quantity: availableQuantity
          }}
        >
          <Form.Item
            name="destination_warehouse_id"
            label="Magazzino Destinazione"
            rules={[{ required: true, message: 'Seleziona il magazzino di destinazione' }]}
          >
            <Select
              placeholder="Seleziona magazzino"
              onChange={handleWarehouseSelect}
            >
              {getFilteredWarehouses().map(warehouse => (
                <Option
                  key={warehouse.id}
                  value={warehouse.id}
                  disabled={originIsCustoms && warehouse.is_nationalized}
                >
                  {warehouse.cod_mag ? `${warehouse.cod_mag} - ` : ''}{warehouse.description}
                  {' '}({warehouse.is_nationalized ? 'Nazionale' : 'Doganale'})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="quantity"
            label="Quantità da Trasferire"
            rules={[
              { required: true, message: 'Inserisci la quantità' },
              { type: 'number', min: 1, message: 'La quantità deve essere almeno 1' },
              {
                validator: (_, value) => {
                  if (value > availableQuantity) {
                    return Promise.reject(`La quantità non può superare ${availableQuantity}`);
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <InputNumber
              min={1}
              max={availableQuantity}
              style={{ width: '100%' }}
              placeholder="Inserisci quantità"
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Motivo Trasferimento"
            rules={[
              { required: true, message: 'Il motivo è obbligatorio' },
              { max: 500, message: 'Motivo troppo lungo (max 500 caratteri)' },
              { whitespace: true, message: 'Il motivo non può essere vuoto' }
            ]}
          >
            <Input
              placeholder="Es: Riorganizzazione magazzino, Evasione ordine, ecc."
              maxLength={500}
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Note Aggiuntive (opzionale)"
            rules={[
              { max: 500, message: 'Note troppo lunghe (max 500 caratteri)' }
            ]}
          >
            <TextArea
              rows={3}
              placeholder="Eventuali note sul trasferimento..."
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>

        <Alert
          message="Il sistema creerà un nuovo stock nel magazzino di destinazione e decrementrà quello di origine. L'operazione verrà registrata nella storia dei movimenti."
          type="info"
          showIcon
        />
      </Space>
    </Modal>
  );
};

export default WarehouseTransferModal;
